import { createGqlClient } from '@ah/design';
import { env } from './env';
import { getSessionToken } from './accounts';

export const gql = createGqlClient(`${env.VITE_API_URL}/graphql`, {
  getToken: () => getSessionToken(),
});

export function authStartUrl(mode: 'desktop' | 'admin' = 'desktop'): string {
  return `${env.VITE_API_URL}/auth/linkedin/start?mode=${mode}`;
}

export async function apiLogout(): Promise<void> {
  const token = getSessionToken();
  await fetch(`${env.VITE_API_URL}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

// ─── Phone / WhatsApp auth ────────────────────────────────────────────────

export async function apiPhoneStart(phone: string): Promise<{ ok: boolean; error?: string }> {
  const res = await fetch(`${env.VITE_API_URL}/auth/phone/start`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone }),
  });
  return res.json();
}

export type PhoneVerifyResult =
  | { status: 'ok'; token: string }
  | { status: 'pending'; pendingToken: string }
  | { status: 'error'; error: string };

export async function apiPhoneVerify(phone: string, code: string): Promise<PhoneVerifyResult> {
  const res = await fetch(`${env.VITE_API_URL}/auth/phone/verify`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone, code }),
  });
  const data = await res.json();
  if (!res.ok) return { status: 'error', error: data.error ?? 'Verification failed' };
  return data;
}

// ─── Onboarding ───────────────────────────────────────────────────────────

export interface LinkedInCandidate {
  linkedinUrl: string;
  name: string;
  headline?: string;
  thumbnailUrl?: string;
}

export async function apiOnboardingSearch(
  pendingToken: string,
  q: string,
): Promise<LinkedInCandidate[]> {
  const res = await fetch(
    `${env.VITE_API_URL}/auth/onboarding/search?q=${encodeURIComponent(q)}`,
    {
      credentials: 'include',
      headers: { Authorization: `Bearer ${pendingToken}` },
    },
  );
  if (!res.ok) return [];
  return res.json();
}

export async function apiOnboardingSetUrl(
  pendingToken: string,
  linkedinUrl: string,
): Promise<{ token?: string; error?: string }> {
  const res = await fetch(`${env.VITE_API_URL}/auth/onboarding/set-url`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${pendingToken}`,
    },
    body: JSON.stringify({ linkedinUrl }),
  });
  return res.json();
}

export interface ProfilePreview {
  name: string | null;
  headline: string | null;
  pictureUrl: string | null;
}

export async function apiOnboardingPreview(
  pendingToken: string,
  linkedinUrl: string,
): Promise<ProfilePreview> {
  const res = await fetch(
    `${env.VITE_API_URL}/auth/onboarding/preview?url=${encodeURIComponent(linkedinUrl)}`,
    {
      credentials: 'include',
      headers: { Authorization: `Bearer ${pendingToken}` },
    },
  );
  if (!res.ok) return { name: null, headline: null, pictureUrl: null };
  return res.json();
}

